import { computed, Injectable, signal } from '@angular/core';

/** Cooldown after a successful `/mfa/enable` before another enable call is allowed. */
export const MFA_ENABLE_COOLDOWN_SEC = 90;

/**
 * Coordinates MFA enrollment UX across the layout overlay and the MFA settings page
 * (pending setup start, enable API cooldown).
 */
@Injectable({ providedIn: 'root' })
export class MfaEnrollmentStateService {
  private cooldownTimer: ReturnType<typeof setInterval> | undefined;

  /** Set by the layout overlay when the user chooses Assign MFA (consumed on the MFA page). */
  private readonly _pendingSetupStart = signal(false);

  /** Seconds remaining until `/mfa/enable` may be called again (0 = allowed). */
  private readonly _enableCooldownSec = signal(0);

  public readonly pendingSetupStart = this._pendingSetupStart.asReadonly();
  public readonly enableCooldownSec = this._enableCooldownSec.asReadonly();

  public readonly canRequestEnable = computed(() => this._enableCooldownSec() <= 0);

  /**
   * Layout overlay: user chose Assign MFA — navigate to MFA route then consume this on the page.
   */
  public requestSetupStart(): void {
    this._pendingSetupStart.set(true);
  }

  /**
   * MFA page: returns whether setup should auto-start, then clears the flag.
   */
  public consumePendingSetupStart(): boolean {
    if (!this._pendingSetupStart()) return false;
    this._pendingSetupStart.set(false);
    return true;
  }

  /**
   * Called after `/mfa/enable` succeeds — blocks further enable calls for 90 seconds.
   */
  public startEnableCooldown(): void {
    this._enableCooldownSec.set(MFA_ENABLE_COOLDOWN_SEC);
    this.clearCooldownTimer();
    this.cooldownTimer = setInterval(() => {
      const next = this._enableCooldownSec() - 1;
      if (next <= 0) {
        this._enableCooldownSec.set(0);
        this.clearCooldownTimer();
        return;
      }
      this._enableCooldownSec.set(next);
    }, 1000);
  }

  /** Clears enable cooldown (e.g. when MFA setup UI is not active). */
  public clearEnableCooldown(): void {
    this._enableCooldownSec.set(0);
    this.clearCooldownTimer();
  }

  private clearCooldownTimer(): void {
    if (this.cooldownTimer !== undefined) {
      clearInterval(this.cooldownTimer);
      this.cooldownTimer = undefined;
    }
  }
}
